'use client';

import { useTransition } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { Drawer } from '../../../common/components/Drawer';
import { OrderByIdTranslations } from '../../utils/getOrderByIdTranslations';
import {
  ButtonBase,
  useHandleGqlErrorsWithSnackbar,
} from '@bts-web/core-features/common';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { cancelOrderAction } from '../../../common/gqlActions/cancelOrder.action';

export interface CancelOrderConfirmationDrawerProps {
  orderId: string;
  translations: Pick<
    OrderByIdTranslations,
    | 'cancelConfirmationTitle'
    | 'cancelConfirmationSubtitle1'
    | 'cancelConfirmationSubtitle2'
    | 'cancelOrder'
    | 'goBack'
  >;
}

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'medium',
  paddingX: 'medium',
  paddingY: 'small',
  height: '100%',
});

const titleStyles = css({
  textAlign: 'center',
  fontSize: 'headline.medium_medium',
  fontWeight: 'headline.medium_medium',
  lineHeight: 'headline.medium_medium',
  letterSpacing: 'headline.medium_medium',
});

const subtitleStyles = css({
  textAlign: 'center',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  lineHeight: 'body.medium',
  letterSpacing: 'body.medium',
});

const buttonsContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small',
  marginTop: 'auto',
});

const CancelOrderConfirmationDrawer = ({
  translations,
  orderId,
}: CancelOrderConfirmationDrawerProps) => {
  const { close } = useModalDrawerContext();

  const [isCancelOrderPending, startTransition] = useTransition();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const onClickCancelOrder = () => {
    startTransition(async () => {
      const response = await cancelOrderAction(orderId);

      if (response.errors) {
        processErrors(response.errors);

        close();

        return;
      }

      close();
    });
  };

  const onClickGoBack = () => {
    close();
  };

  return (
    <Drawer
      showBorderBottom={false}
      title={
        <h3 className={titleStyles}>{translations.cancelConfirmationTitle}</h3>
      }
    >
      <div className={containerStyles}>
        <p className={subtitleStyles}>
          {translations.cancelConfirmationSubtitle1}{' '}
          {translations.cancelConfirmationSubtitle2}
        </p>
        <div className={buttonsContainerStyles}>
          <ButtonBase
            fullWidth
            size="large"
            visual="primary"
            isLoading={isCancelOrderPending}
            disabled={isCancelOrderPending}
            onClick={onClickCancelOrder}
          >
            {translations.cancelOrder}
          </ButtonBase>
          <ButtonBase
            fullWidth
            size="large"
            visual="secondary"
            onClick={onClickGoBack}
            disabled={isCancelOrderPending}
          >
            {translations.goBack}
          </ButtonBase>
        </div>
      </div>
    </Drawer>
  );
};

export { CancelOrderConfirmationDrawer };
